import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
import { AccessRole, UserData } from '../../shared/models/LoginData';
import { CookieService } from 'ngx-cookie-service';
import { AppSettingsService } from '../app-settings/app-settings.service';
import { Router } from '@angular/router';
import { UserProfile } from '@deltager/organizers/admin/shared/models/UserProfile';
import { map } from 'rxjs/operators';
import { Observable, firstValueFrom } from 'rxjs';
import { UserPwdChange } from '@deltager/organizers/admin/shared/models/UserPwdChange';
import { AppSettings } from '../app-settings/app-settings';
import { OrganizersService } from './organizers.service';
import { Store } from '@ngrx/store';
import { OrganizerSettings } from '../../shared/models/OrganizerSettings';
import { DntEventsListState } from '@deltager/organizers/admin/modules/events/events-list-main/dnt-events-list/store/dnt-events-list.state';
import { ResetDntFiltersAction } from '@deltager/organizers/admin/modules/events/events-list-main/dnt-events-list/store/dnt-events-list.actions';
import { CachedNetworkRequestService } from '../../shared/services/cached-network-request.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly LIMITED_ACCESS_ROLES: AccessRole[] = [AccessRole.User, AccessRole.Limited, AccessRole.Finance];

  private readonly Endpoints = {
    USER_DATA: `${environment.authApiURL}/userdata`,
    USER_SWITCH_ORGANIZER: `${environment.oldApiBaseURL}/user/switchOrg/`,
    USER_PROFILE: `${environment.apiBaseURL}/user/profile/`,
    USER_PASSWORD: `${environment.apiBaseURL}/user/pwd/`,
    USER_FOTOWEB: `${environment.apiBaseURL}/user/fotoweb/`,
  };

  private _userData?: UserData;
  private _organizerId = 0;
  private _settings: AppSettings;
  private _sessionId: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cookieService: CookieService,
    private applicationService: AppSettingsService,
    private router: Router,
    private organizersService: OrganizersService,
    private store: Store<{ dntEventsList: DntEventsListState }>,
    private cachedNetworkRequest: CachedNetworkRequestService,
  ) {
    this.applicationService.Settings.subscribe(v => (this._settings = v));
  }

  get OrganizerId(): number {
    return this._organizerId;
  }

  set OrganizerId(organizerId: number) {
    this._organizerId = organizerId;
  }

  get SessionId() {
    this._sessionId = this.cookieService.get('SID' + this._settings.affiliateId);
    if (this._sessionId === '') {
      const random = Number.parseInt((Math.random() * 2147483647).toString(), 0).toString();
      const ticks = 621356076000000000 + new Date().getTime() * 10000;
      this.SessionId = ticks + ('0000000000' + random).substring(random.length);
    }
    return this._sessionId;
  }

  set SessionId(sessionId: string) {
    this._sessionId = sessionId;
    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + 15); // 15 min
    this.cookieService.set('SID' + this._settings.affiliateId, String(sessionId), expiresAt, '/');
  }

  public Init(): void {}

  public async switchOrganizer(organizerId: number): Promise<boolean> {
    // If legacy change url, else change route and token
    if (this._settings.launchedFromLegacySite) {
      const url = this._settings.rootUrl + '/arrangement/clubs.aspx?id=' + organizerId;
      this.applicationService.goToExternalUrl(url);
    } else {
      // Set organizer cookie
      this.setOrganizerId(organizerId);

      // Reload userdata
      this._userData = null;
      await this.getUserData();

      // Get a new token with organizer Id embedded
      await this.authService.setClubIdInToken(organizerId);
      this.store.dispatch(new ResetDntFiltersAction());

      this.router.navigate(['/']);
    }

    return true;
  }

  public getUserDataInstant(): UserData {
    return this._userData as UserData;
  }

  public isLimitedAccessRole(allowRoles: AccessRole[] = []): boolean {
    const limitedRoles = this.LIMITED_ACCESS_ROLES.filter(r => !allowRoles.includes(r));

    return !!this._userData && limitedRoles.includes(this._userData.accessRole);
  }

  public async getUserData(canUseCache = true): Promise<UserData> {
    let data: UserData = null;
    const affID = this._settings.affiliateId;

    if (+affID < 1) {
      return data;
    }

    if (this._userData && canUseCache) {
      this._userData = data = Object.assign(new UserData(), this._userData);

      if (this.OrganizerId <= 0) {
        await firstValueFrom(this.setOrganizer(data));
      }
    } else {
      // Check if there is an existing organizerId
      this.verifyOrganizerId();

      const httpData = await this.cachedNetworkRequest
        .get<UserData>(`${this.Endpoints.USER_DATA}?organizerId=${this.OrganizerId}&ignoreCache=true`)
        .toPromise();

      if (httpData.userId > 0) {
        this._userData = data = Object.assign(new UserData(), httpData);

        await firstValueFrom(this.setOrganizer(data));
      }
    }
    return data;
  }

  public getUserProfile(userId: number): Observable<UserProfile> {
    const url = `${this.Endpoints.USER_PROFILE}${userId}?ignoreCache=true`;
    return this.http.get(url).pipe(map(resoponse => resoponse as UserProfile));
  }

  public updateUserProfile(userProfile: UserProfile): Observable<boolean> {
    const url = `${this.Endpoints.USER_PROFILE}`;
    return this.http.put(url, userProfile).pipe(map(response => response as boolean));
  }

  public updateUserPassword(userPwdChange: UserPwdChange): Observable<boolean> {
    const url = `${this.Endpoints.USER_PASSWORD}`;
    return this.http.put(url, userPwdChange).pipe(map(response => response as boolean));
  }

  public fotoWebLogout(): Promise<number> {
    const url = `${this.Endpoints.USER_FOTOWEB}logout`;
    return firstValueFrom(this.http.post<number>(url, null));
  }

  public isLoggedIntoFotoWeb(): Promise<boolean> {
    const url = `${this.Endpoints.USER_FOTOWEB}is-logged-in`;
    return firstValueFrom(this.http.get<boolean>(url));
  }

  private setOrganizer(userData: UserData): Observable<OrganizerSettings> {
    this.setOrganizerId(userData.clubId);

    return this.organizersService.getOrganizerSettingsById(userData.clubId);
  }

  private verifyOrganizerId() {
    if (!this._organizerId || this._organizerId === 0 || this._organizerId === null) {
      // Try to fetch from cookie if not provided
      this._organizerId = Number.parseInt(this.cookieService.get('club_' + this._settings.affiliateId), 0) || 0;
    }
  }

  private setOrganizerId(orgId: number): void {
    // Never change organizerId from app explicitly when integrated with legacy
    if (this.applicationService.settingsValue.launchedFromLegacySite) {
      return;
    }

    if (orgId === 0) {
      return;
    }

    this.OrganizerId = orgId;
    this.cookieService.set('club_' + this._settings.affiliateId, String(orgId), 50, '/'); // 50 days
  }
}
